/**
 * DefencIQ Domain Weights Configuration
 * Exact weights as specified - do not modify
 */

export const DOMAIN_WEIGHTS = {
  organisation_profile: 5,
  identity_and_mfa: 14,
  endpoint_management: 9,
  application_control: 14,
  patch_management: 14,
  privileged_access: 9,
  office_macros_and_user_hardening: 7,
  backups: 9,
  logging_and_monitoring: 6,
  vulnerability_management: 3,
  compliance_and_governance: 2,
  policy_governance: 8,
  disp_scope: 4,
  physical_security: 4,
  personnel_security: 8,
  foci: 6
};

export const READINESS_BANDS = {
  not_ready: { min: 0, max: 39 },
  partially_ready: { min: 40, max: 64 },
  substantially_ready: { min: 65, max: 84 },
  ready_for_validation: { min: 85, max: 100 }
};

export const DOMAIN_MATURITY_BANDS = {
  poor: [0, 39],
  developing: [40, 64],
  moderate: [65, 84],
  strong: [85, 100]
};

export const COMPONENT_LABELS = {
  microsoft_entra_id: "Microsoft Entra ID",
  conditional_access: "Conditional Access",
  microsoft_intune: "Microsoft Intune",
  microsoft_defender_for_endpoint: "Microsoft Defender for Endpoint",
  wdac_or_applocker: "WDAC / AppLocker",
  tenable: "Tenable",
  qualys: "Qualys",
  microsoft_sentinel: "Microsoft Sentinel",
  privileged_access_management: "Privileged Access Management",
  backup_platform: "Backup Platform",
  drata: "Drata",
  remediation_tracker: "Remediation Tracker",
  evidence_pack_generator: "Evidence Pack Generator",
  disp_dashboard: "DISP Dashboard"
};

export const LAYER_MAP = {
  enforcement_layer: [
    "microsoft_entra_id",
    "conditional_access",
    "microsoft_intune",
    "microsoft_defender_for_endpoint",
    "wdac_or_applocker",
    "privileged_access_management",
    "backup_platform"
  ],
  validation_layer: [
    "tenable",
    "qualys"
  ],
  monitoring_layer: [
    "microsoft_sentinel"
  ],
  compliance_layer: [
    "drata",
    "remediation_tracker",
    "evidence_pack_generator",
    "disp_dashboard"
  ]
};

export const LAYER_LABELS = {
  enforcement_layer: "Enforcement Layer",
  validation_layer: "Validation Layer",
  monitoring_layer: "Monitoring Layer",
  compliance_layer: "Compliance Layer"
};

// Critical gating question IDs - these control overall status caps
export const CRITICAL_GATING_QUESTIONS = [
  'mfa_all_users',
  'mfa_privileged_users',
  'central_device_management',
  'device_compliance_policies',
  'allow_listing_in_place',
  'restrict_which_apps_can_run',
  'critical_patch_sla',
  'central_patch_compliance_tracking',
  'offline_or_immutable_backups',
  'last_successful_restore_test',
  'separate_admin_accounts',
  'admin_activity_logged_and_reviewed',
  'internet_macros_blocked',
  'pre_employment_screening',
  'baseline_vetting_required',
  'offboarding_controls'
];

// Section display configuration
export const SECTION_CONFIG = {
  engagement: {
    title: "Engagement Details",
    description: "Basic engagement information and assessment context",
    icon: "FileText",
    scored: false
  },
  organisation_profile: {
    title: "Organisation Profile",
    description: "Organisation profile and environment baseline",
    icon: "Building2",
    scored: true
  },
  current_tooling: {
    title: "Current Tooling & Platforms",
    description: "Current security tooling, deployment coverage, and operational maturity across the recommended ML2 technology stack.",
    icon: "Layers3",
    scored: false
  },
  identity_and_mfa: {
    title: "Identity & MFA",
    description: "Identity and Multi-Factor Authentication readiness",
    icon: "KeyRound",
    scored: true
  },
  endpoint_management: {
    title: "Endpoint Management",
    description: "Endpoint management and device compliance readiness",
    icon: "Monitor",
    scored: true
  },
  application_control: {
    title: "Application Control",
    description: "Application control and execution restrictions",
    icon: "Shield",
    scored: true
  },
  patch_management: {
    title: "Patch Management",
    description: "Patch management processes and SLAs",
    icon: "RefreshCw",
    scored: true
  },
  privileged_access: {
    title: "Privileged Access",
    description: "Privileged access management and admin controls",
    icon: "UserCog",
    scored: true
  },
  office_macros_and_user_hardening: {
    title: "Office Macros & User Hardening",
    description: "Office macro restrictions and browser security",
    icon: "FileSpreadsheet",
    scored: true
  },
  backups: {
    title: "Backups",
    description: "Backup and restore readiness",
    icon: "Database",
    scored: true
  },
  logging_and_monitoring: {
    title: "Logging & Monitoring",
    description: "Centralised logging and incident monitoring",
    icon: "Activity",
    scored: true
  },
  vulnerability_management: {
    title: "Vulnerability Management",
    description: "Vulnerability scanning and remediation tracking",
    icon: "AlertTriangle",
    scored: true
  },
  compliance_and_governance: {
    title: "Compliance & Governance",
    description: "Compliance frameworks and evidence collection",
    icon: "ClipboardCheck",
    scored: true
  },
  policy_governance: {
    title: "Policy & Governance",
    description: "Security policy coverage, control governance, enforcement, and audit readiness",
    icon: "ShieldCheck",
    scored: true
  },
  pain_points: {
    title: "Pain Points & Drivers",
    description: "Client challenges, compliance pressure, and engagement drivers",
    icon: "MessageCircle",
    scored: true
  },
  evidence_readiness: {
    title: "Evidence Readiness",
    description: "Discovery-level check that confirms whether major categories of supporting evidence exist. Detailed validation occurs during Accelerator.",
    icon: "FileCheck",
    scored: false
  },
  disp_scope: {
    title: "Contracts, Classification & DISP Scope",
    description: "Defence engagement, classification expectations, and DISP applicability",
    icon: "Landmark",
    scored: true
  },
  physical_security: {
    title: "Physical Security & Facilities",
    description: "Physical presence and facility access controls",
    icon: "Home",
    scored: true
  },
  personnel_security: {
    title: "Personnel Security & Clearances",
    description: "Personnel vetting, security clearances, insider threat mitigation, and workforce trust controls aligned to DISP requirements",
    icon: "UserCheck",
    scored: true
  },
  foci: {
    title: "Foreign Ownership, Control & Influence (FOCI)",
    description: "Assessment of foreign ownership, control, and influence risks",
    icon: "Globe",
    scored: true
  }
};

export default {
  DOMAIN_WEIGHTS,
  READINESS_BANDS,
  DOMAIN_MATURITY_BANDS,
  COMPONENT_LABELS,
  LAYER_MAP,
  LAYER_LABELS,
  CRITICAL_GATING_QUESTIONS,
  SECTION_CONFIG
};
